import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  AlertCircle, AlertTriangle, CheckCircle, X,
} from 'react-feather';
import toast, { Toaster, ToastPositions } from '../toast';
import IconWrapper, { IconColors, IconSizes } from '../icon-wrapper';
import './notification.scss';
import useTranslation from '../../../utils/translation-utils';

const DEFAULT_DURATION = 4000;

const NotificationTypes = {
  SUCCESS: 'noti-success',
  WARNING: 'noti-warning',
  ERROR: 'noti-error',
  CUSTOM: 'noti-custom',
};

const NotificationTestIds = {
  TEXT: 'text',
  SUPPORTING_TEXT: 'supporting_text',
  CLOSE_ICON: 'close_toast_icon',
  CONTAINER: 'toast_container',
  ICON: 'toast_icon',
  BODY: 'toast_body',
};

/*
  Toasts are mounted as new DOM nodes with their text already in place, so the aria-live
  attributes on the toast itself have no change to report and screen readers stay silent.
  These regions stay mounted for the lifetime of the app, so writing a message into one is a
  change the screen reader picks up.
*/
let announceMessage = null;

const NotificationAnnouncer = () => {
  const [messages, setMessages] = useState({ polite: '', assertive: '' });
  const announceCounts = useRef({ polite: 0, assertive: 0 });

  useEffect(() => {
    announceMessage = ({ message, isAssertive }) => {
      const region = isAssertive ? 'assertive' : 'polite';
      // A screen reader stays quiet when a region repeats the string it last read out, which
      // would silence a second identical toast. A zero-width space is appended to every other
      // message so consecutive strings always differ. It is invisible and is not spoken.
      announceCounts.current[region] += 1;
      const padded = announceCounts.current[region] % 2 === 1;
      const value = padded ? `${message}\u200B` : message;
      setMessages((current) => ({ ...current, [region]: value }));
    };
    return () => {
      announceMessage = null;
    };
  }, []);

  return (
    <>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {messages.polite}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        {messages.assertive}
      </div>
    </>
  );
};

const announce = (message, isAssertive) => {
  if (announceMessage) announceMessage({ message, isAssertive });
};

const Notification = ({
  thisToast, type, text, supportingText, customView, showClose, showIcon, testIds,
}) => {
  const [getTranslation] = useTranslation();
  const bodyClassNames = clsx('noti-body', { 'noti-no-sup-text': !supportingText?.trim() });

  const announced = useRef(false);

  useEffect(() => {
    // StrictMode runs mount effects twice in development; the ref holds each toast to one
    // announcement. Custom toasts carry a view rather than text, so they have nothing to read.
    if (announced.current || type === NotificationTypes.CUSTOM) return;
    announced.current = true;
    announce(
      [getTranslation(text), getTranslation(supportingText)].filter(Boolean).join('. '),
      type !== NotificationTypes.SUCCESS,
    );
  }, []);

  const getIcon = () => {
    let iconType;
    let iconColor;

    switch (type) {
      case NotificationTypes.ERROR:
        iconType = AlertCircle;
        iconColor = IconColors.ERROR;
        break;
      case NotificationTypes.WARNING:
        iconType = AlertTriangle;
        iconColor = IconColors.WARNING;
        break;
      case NotificationTypes.SUCCESS:
        iconType = CheckCircle;
        iconColor = IconColors.SUCCESS;
        break;
      default:
        return null;
    }

    return (
      <div className="noti-icon" data-testid={NotificationTestIds.ICON}>
        <IconWrapper icon={iconType} color={iconColor} size={IconSizes.MEDIUM} />
      </div>
    );
  };

  const getBody = () => {
    switch (type) {
      case NotificationTypes.ERROR:
      case NotificationTypes.WARNING:
      case NotificationTypes.SUCCESS:
        return (
          <div className={bodyClassNames}>
            <div
              data-testid={testIds && testIds[NotificationTestIds.TEXT]}
              className="noti-text"
            >
              {getTranslation(text)}
            </div>
            <div
              data-testid={testIds && testIds[NotificationTestIds.SUPPORTING_TEXT]}
              className="noti-supporting-text"
            >
              {getTranslation(supportingText)}
            </div>
          </div>
        );
      case NotificationTypes.CUSTOM:
        return (
          <div className="noti-body" data-testid={NotificationTestIds.BODY}>
            {customView}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`noti-cont ${type}`} data-testid={NotificationTestIds.CONTAINER}>
      {showIcon && getIcon()}
      {getBody()}
      {showClose ? (
        <IconWrapper
          className="noti-close-icon"
          icon={X}
          size={IconSizes.MEDIUM}
          color={IconColors.GREY}
          onClick={() => toast.dismiss(thisToast.id)}
          testIds={NotificationTestIds.CLOSE_ICON}
        />
      ) : null}
    </div>
  );
};

Notification.propTypes = {
  thisToast: PropTypes.shape({ id: PropTypes.string }),
  type: PropTypes.oneOf(Object.values(NotificationTypes)),
  text: PropTypes.string,
  supportingText: PropTypes.string,
  customView: PropTypes.element,
  showClose: PropTypes.bool,
  showIcon: PropTypes.bool,
  testIds: PropTypes.shape({}),
};

Notification.defaultProps = {
  thisToast: {},
  type: NotificationTypes.SUCCESS,
  text: '',
  supportingText: '',
  customView: null,
  showClose: true,
  showIcon: true,
  testIds: {},
};

// react-hot-toast seeds each toast with ariaProps { role: 'status', 'aria-live': 'polite' }
// and then Object.assigns the caller's options over it — so passing ariaProps REPLACES that
// default rather than merging into it. Omitting role/aria-live here leaves every toast
// unannounced. Errors and warnings interrupt; success and info wait their turn.
const politeAriaProps = {
  className: 'noti-toast-aria',
  role: 'status',
  'aria-live': 'polite',
  'aria-atomic': 'true',
};

const assertiveAriaProps = {
  className: 'noti-toast-aria',
  role: 'alert',
  'aria-live': 'assertive',
  'aria-atomic': 'true',
};

const NotificationBase = () => (
  <>
    <NotificationAnnouncer />
    <Toaster />
  </>
);

const commonToastProperties = {
  duration: DEFAULT_DURATION,
  position: ToastPositions.TOP_RIGHT,
  className: 'noti-toast',
  ariaProps: politeAriaProps,
};

const notification = {
  success: (text, supportingText, showIcon, testIds) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.SUCCESS}
      showIcon={showIcon}
      testIds={testIds}
    />
  ), {
    ...commonToastProperties,
  }),
  error: (text, supportingText, showIcon, testIds) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.ERROR}
      showIcon={showIcon}
      testIds={testIds}
    />
  ), {
    ...commonToastProperties,
    ariaProps: assertiveAriaProps,
    duration: 8000,
  }),
  warning: (text, supportingText, showIcon, testIds) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.WARNING}
      showIcon={showIcon}
      testIds={testIds}
    />
  ), {
    ...commonToastProperties,
    ariaProps: assertiveAriaProps,
  }),
  custom: (view, duration, showClose, showIcon, testIds) => toast((t) => (
    <Notification
      thisToast={t}
      customView={view}
      type={NotificationTypes.CUSTOM}
      showClose={showClose}
      showIcon={showIcon}
      testIds={testIds}
    />
  ), {
    ...commonToastProperties,
    className: 'noti-toast noti-custom',
    duration: duration || DEFAULT_DURATION,
  }),
  dismiss: (id) => toast.dismiss(id),
  remove: (id) => toast.remove(id),
};

export {
  notification as default,
  NotificationTypes,
  NotificationBase,
  NotificationTestIds,
};
